export const COMPLIMENT_RELATIONSHIPS = Object.freeze([
  { key: 'stranger', label: 'Stranger' },
  { key: 'coworker', label: 'Coworker' },
  { key: 'friend', label: 'Friend' },
  { key: 'family', label: 'Family' },
  { key: 'partner', label: 'Partner' },
])

export function complimentCountForLog(log) {
  const eventCount = Array.isArray(log?.compliment_events)
    ? log.compliment_events.length
    : Number(log?.compliment_event_count || 0)
  return eventCount > 0 ? eventCount : Number(log?.legacy_compliments ?? log?.compliments ?? 0)
}

export function complimentSourceBreakdown(log) {
  if (log?.compliment_source_breakdown) return log.compliment_source_breakdown
  return (log?.compliment_events || []).reduce((counts, event) => {
    counts[event.relationship_type] = (counts[event.relationship_type] || 0) + 1
    return counts
  }, {})
}

export function defaultComplimentContext(log) {
  const tags = Array.isArray(log?.context_tags) ? log.context_tags : []
  return tags.at(-1)?.context_tag || tags.at(-1)?.tag || null
}

export function hydrateComplimentData(
  wearLogs,
  { events = [], wearContexts = [], whoLabels = [], whereLabels = [] } = {},
) {
  const whoById = new Map(whoLabels.map(label => [label.id, label.label]))
  const whereById = new Map(whereLabels.map(label => [label.id, label.label]))
  const eventsByWear = new Map()
  const contextsByWear = new Map()

  for (const event of events) {
    const hydrated = {
      ...event,
      who_note: event.who_label_id ? whoById.get(event.who_label_id) || null : null,
      where_label: event.where_label_id ? whereById.get(event.where_label_id) || null : null,
    }
    eventsByWear.set(event.wear_log_id, [...(eventsByWear.get(event.wear_log_id) || []), hydrated])
  }
  for (const context of wearContexts) {
    contextsByWear.set(context.wear_log_id, [...(contextsByWear.get(context.wear_log_id) || []), context])
  }

  return wearLogs.map(log => {
    const complimentEvents = eventsByWear.get(log.id) || []
    const legacyCompliments = Number(log.compliments || 0)
    const breakdown = complimentEvents.reduce((counts, event) => {
      counts[event.relationship_type] = (counts[event.relationship_type] || 0) + 1
      return counts
    }, {})
    return {
      ...log,
      legacy_compliments: legacyCompliments,
      compliment_events: complimentEvents,
      compliment_event_count: complimentEvents.length,
      compliment_count: complimentEvents.length > 0 ? complimentEvents.length : legacyCompliments,
      compliment_source_breakdown: breakdown,
      context_tags: contextsByWear.get(log.id) || [],
    }
  })
}

export async function createComplimentEvent(client, wearLogId, {
  relationshipType,
  contextTag = null,
  whoNote = null,
  whereLabel = null,
  occurredAt = null,
} = {}) {
  const { data, error } = await client.rpc('add_compliment_event', {
    p_wear_log_id: wearLogId,
    p_relationship_type: relationshipType,
    p_context_tag: contextTag || null,
    p_who_note: whoNote?.trim() || null,
    p_where_label: whereLabel?.trim() || null,
    p_occurred_at: occurredAt || null,
  })
  if (error) throw error
  return data
}

// Removes one of the signed-in owner's own compliments (Undo, or a mistaken
// double entry). Row security already limits deletes to the owner; the explicit
// user_id filter keeps that true even if a policy were ever loosened.
export async function deleteComplimentEvent(client, userId, eventId) {
  if (!userId || !eventId) throw new Error('Choose a compliment in your account first.')
  const { data, error } = await client.from('compliment_events')
    .delete().eq('id', eventId).eq('user_id', userId).select('id')
  if (error) throw error
  if (!data?.length) throw new Error('That compliment was not removed. Pull to refresh and try again.')
  return data[0].id
}

export async function fetchCanonicalContextTags(client) {
  const { data, error } = await client
    .from('wear_context_tags')
    .select('tag,display_name,sort_order')
    .eq('is_active', true)
    .order('sort_order')
  if (error) throw error
  return data || []
}

// Hydrate every referenced private label, while callers can retain the separate
// recent-12 suggestion lists. Every lookup is explicitly owner scoped.
export async function referencedComplimentLabels(client, userId, events, recent = {}) {
  const result = {}
  for (const [kind, field] of [['who', 'who_label_id'], ['where', 'where_label_id']]) {
    const labels = [...(recent[kind] || [])]
    const ids = [...new Set(events.map(event => event[field]).filter(Boolean))].filter(id => !labels.some(label => label.id === id))
    for (let offset = 0; offset < ids.length; offset += 100) {
      const { data, error } = await client.from(`compliment_${kind}_labels`).select('id,label').eq('user_id', userId).in('id', ids.slice(offset, offset + 100))
      if (error) throw error
      labels.push(...(data || []))
    }
    result[kind] = labels
  }
  return result
}

export async function readWearCompliments(client, userId, wearLogId) {
  if (!userId || !wearLogId) throw new Error('Choose a wear in your account first.')
  const { data, error } = await client.from('compliment_events')
    .select('id,wear_log_id,relationship_type,occurred_at,created_at,context_tag,who_label_id,where_label_id')
    .eq('user_id', userId).eq('wear_log_id', wearLogId).order('created_at', { ascending: false })
  if (error) throw error
  const events = data || []
  const labels = await referencedComplimentLabels(client, userId, events)
  return hydrateComplimentData([{ id: wearLogId }], { events, whoLabels: labels.who, whereLabels: labels.where })[0].compliment_events
}
